import React, { useState, useEffect } from 'react';
import { User, Mail, Phone, Building, Shield, Camera, Edit2, Save, X, Key, Calendar } from 'lucide-react';

import API_BASE_URL from "../config";

export default function MyProfile() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [profileImage, setProfileImage] = useState(null);
  const [digitalSignImage, setDigitalSignImage] = useState(null);
  const [profileFile, setProfileFile] = useState(null);
  const [signFile, setSignFile] = useState(null);

  // Get userId from localStorage (from the logged-in user)
  const getUserId = () => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        console.log("Logged in user data:", parsedUser);
        return parsedUser.userId;
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }
    return null;
  };

  const userId = getUserId();

  useEffect(() => {
    if (userId) {
      fetchUserData();
    } else {
      console.error("No user ID found in localStorage");
      setLoading(false);
    }
  }, [userId]);

  const fetchUserData = async () => {
    setLoading(true);
    try {
      console.log(`Fetching user data for userId: ${userId}`);
      const response = await fetch(`${API_BASE_URL}api/Users/${userId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log("Fetched user data:", data);
      
      setUser(data);
      setFormData(data);

      // Set profile image
      if (data.profileBase64) {
        setProfileImage(`data:image/jpeg;base64,${data.profileBase64}`);
      }

      // Set digital signature
      if (data.digitalSignFileBase64) {
        setDigitalSignImage(`data:${data.digitalSignContentType};base64,${data.digitalSignFileBase64}`);
      }
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      alert('Failed to load profile data. Please try logging in again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleProfileImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSignImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSignFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setDigitalSignImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getLicenseStatus = () => {
    if (!user.key_expired) return { text: 'No expiration set', color: 'gray' };
    
    const now = new Date();
    const expiry = new Date(user.key_expired);
    const daysRemaining = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    
    if (daysRemaining < 0) {
      return { text: 'Expired', color: 'red' };
    } else if (daysRemaining <= 7) {
      return { text: `Expires in ${daysRemaining} day(s)`, color: 'yellow' };
    } else {
      return { text: 'Active', color: 'green' };
    }
  };

  const handleSave = async () => {
    try {
      // Update user details
      const formDataToSend = new FormData();
      formDataToSend.append('Username', formData.username);
      formDataToSend.append('Password', formData.password);
      formDataToSend.append('FirstName', formData.firstName);
      formDataToSend.append('LastName', formData.lastName);
      formDataToSend.append('Email', formData.email);
      formDataToSend.append('PositionId', formData.positionId);
      formDataToSend.append('DepartmentId', formData.departmentId);
      formDataToSend.append('UserLevelId', formData.userLevelId);
      formDataToSend.append('IsActive', formData.isActive);
      formDataToSend.append('ContactNo', formData.contactNo);
      
      // Keep existing license info (don't modify)
      if (user.licenseKey) {
        formDataToSend.append('LicenseKey', user.licenseKey);
      }
      if (user.key_expired) {
        formDataToSend.append('Key_expired', user.key_expired);
      }

      if (profileFile) {
        formDataToSend.append('Profile', profileFile);
      }

      if (signFile) {
        formDataToSend.append('DigitalSignFile', signFile);
      }

      const response = await fetch(`${API_BASE_URL}api/Users/${userId}`, {
        method: 'PUT',
        body: formDataToSend,
      });

      if (response.ok) {
        alert('Profile updated successfully!');
        setEditMode(false);
        fetchUserData();
      } else {
        throw new Error('Failed to update profile');
      }
    } catch (error) {
      console.error('Failed to update profile:', error);
      alert('Failed to update profile. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-blue-50 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!userId || !user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">No User Data Found</h2>
          <p className="text-gray-600">Please log in again to view your profile.</p>
        </div>
      </div>
    );
  }

  const licenseStatus = getLicenseStatus();

  return (
    <div className="min-h-screen from-indigo-50 via-white to-blue-50 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800">My Profile</h1>
          <p className="text-gray-600 mt-1">Manage your personal information</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Profile Picture & Digital Signature */}
          <div className="space-y-6">
            {/* Profile Picture Card */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-indigo-600" />
                Profile Picture
              </h3>
              
              <div className="relative group">
                <div className="w-48 h-48 mx-auto rounded-2xl overflow-hidden bg-gradient-to-br from-indigo-100 to-blue-100 flex items-center justify-center border-4 border-white shadow-xl">
                  {profileImage ? (
                    <img src={profileImage} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-24 h-24 text-indigo-300" />
                  )}
                </div>
                
                {editMode && (
                  <label className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                    <Camera className="w-12 h-12 text-white" />
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleProfileImageChange}
                    />
                  </label>
                )}
              </div>

              <div className="mt-4 text-center">
                <h4 className="text-xl font-bold text-gray-800">
                  {user.firstName} {user.lastName}
                </h4>
                <p className="text-sm text-gray-500 mt-1">{user.positionId}</p>
              </div>
            </div>

            {/* Digital Signature Card */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Edit2 className="w-5 h-5 text-purple-600" />
                Digital Signature
              </h3>
              
              <div className="relative group">
                <div className="w-full h-32 rounded-xl overflow-hidden bg-gray-50 flex items-center justify-center border-2 border-dashed border-gray-300">
                  {digitalSignImage ? (
                    <img src={digitalSignImage} alt="Digital Signature" className="max-w-full max-h-full object-contain" />
                  ) : (
                    <Edit2 className="w-12 h-12 text-gray-300" />
                  )}
                </div>
                
                {editMode && (
                  <label className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                    <Camera className="w-8 h-8 text-white" />
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleSignImageChange}
                    />
                  </label>
                )}
              </div>

              {editMode && (
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Click to upload signature
                </p>
              )}
            </div>

            {/* License Information Card */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Key className="w-5 h-5 text-amber-600" />
                License Information
              </h3>
              
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-gray-500 mb-1">License Key</p>
                  <p className="text-sm font-mono bg-gray-50 px-3 py-2 rounded-lg text-gray-800 break-all">
                    {user.licenseKey || 'Not assigned'}
                  </p>
                </div>
                
                <div>
                  <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    Expiration Date
                  </p>
                  <p className="text-sm bg-gray-50 px-3 py-2 rounded-lg text-gray-800">
                    {formatDate(user.key_expired)}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-gray-500 mb-1">Status</p>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                    licenseStatus.color === 'green' ? 'bg-green-100 text-green-700' :
                    licenseStatus.color === 'yellow' ? 'bg-yellow-100 text-yellow-700' :
                    licenseStatus.color === 'red' ? 'bg-red-100 text-red-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {licenseStatus.text}
                  </span>
                </div>

                <p className="text-xs text-gray-400 italic mt-2">
                  * License information can only be modified by administrators
                </p>
              </div>
            </div>
          </div>

          {/* Right Column - User Details */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-semibold text-gray-800 text-lg flex items-center gap-2">
                  <Shield className="w-5 h-5 text-indigo-600" />
                  Personal Information
                </h3>
                
                {!editMode ? (
                  <button
                    onClick={() => setEditMode(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit Profile
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={handleSave}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all"
                    >
                      <Save className="w-4 h-4" />
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setEditMode(false);
                        setFormData(user);
                        fetchUserData();
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all"
                    >
                      <X className="w-4 h-4" />
                      Cancel
                    </button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Username */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-600 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Username
                  </label>
                  {editMode ? (
                    <input
                      type="text"
                      name="username"
                      value={formData.username || ''}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="px-4 py-2 bg-gray-50 rounded-lg text-gray-800">{user.username}</p>
                  )}
                </div>

                {/* Password */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-600 flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Password
                  </label>
                  {editMode ? (
                    <input
                      type="password"
                      name="password"
                      value={formData.password || ''}
                      onChange={handleInputChange}
                      placeholder="Enter new password"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="px-4 py-2 bg-gray-50 rounded-lg text-gray-800">••••••••</p>
                  )}
                </div>

                {/* First Name */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-600">First Name</label>
                  {editMode ? (
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName || ''}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="px-4 py-2 bg-gray-50 rounded-lg text-gray-800">{user.firstName}</p>
                  )}
                </div>

                {/* Last Name */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-600">Last Name</label>
                  {editMode ? (
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName || ''}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="px-4 py-2 bg-gray-50 rounded-lg text-gray-800">{user.lastName}</p>
                  )}
                </div>

                {/* Email */}
                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-medium text-gray-600 flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email Address
                  </label>
                  {editMode ? (
                    <input
                      type="email"
                      name="email"
                      value={formData.email || ''}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="px-4 py-2 bg-gray-50 rounded-lg text-gray-800">{user.email}</p>
                  )}
                </div>

                {/* Contact Number */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-600 flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Contact Number
                  </label>
                  {editMode ? (
                    <input
                      type="text"
                      name="contactNo"
                      value={formData.contactNo || ''}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="px-4 py-2 bg-gray-50 rounded-lg text-gray-800">{user.contactNo}</p>
                  )}
                </div>

                {/* Position */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-600 flex items-center gap-2">
                    <Building className="w-4 h-4" />
                    Position
                  </label>
                  {editMode ? (
                    <input
                      type="text"
                      name="positionId"
                      value={formData.positionId || ''}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="px-4 py-2 bg-gray-50 rounded-lg text-gray-800">{user.positionId}</p>
                  )}
                </div>

                {/* Department ID */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-600">Department ID</label>
                  {editMode ? (
                    <input
                      type="number"
                      name="departmentId"
                      value={formData.departmentId || ''}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="px-4 py-2 bg-gray-50 rounded-lg text-gray-800">{user.departmentId}</p>
                  )}
                </div>

                {/* User Level ID */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-600">User Level ID</label>
                  {editMode ? (
                    <input
                      type="number"
                      name="userLevelId"
                      value={formData.userLevelId || ''}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  ) : (
                    <p className="px-4 py-2 bg-gray-50 rounded-lg text-gray-800">{user.userLevelId}</p>
                  )}
                </div>

                {/* Status */}
                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-medium text-gray-600">Account Status</label>
                  <div className="px-4 py-2 bg-gray-50 rounded-lg">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                      user.isActive === 1 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {user.isActive === 1 ? '✓ Active' : '✗ Inactive'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}